
import React, { useState } from 'react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command';
import { 
  Search, 
  Home, 
  Calendar, 
  Building2, 
  Percent, 
  Grid3X3,
  CalendarRange,
  Plus,
  Settings,
  Users,
  BarChart3,
  Clock,
  X,
  Zap
} from 'lucide-react';
import { useSearchContext } from './SearchProvider';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

const quickActions = [
  { id: 'create-booking', title: 'Создать бронь', icon: Plus, url: '/admin/bookings/new', category: 'actions' },
  { id: 'add-room', title: 'Добавить номер', icon: Building2, url: '/admin/rooms/new', category: 'actions' },
  { id: 'create-offer', title: 'Создать предложение', icon: Percent, url: '/admin/offers/new', category: 'actions' },
];

const navigationItems = [
  { id: 'room-grid', title: 'Шахматка номеров', icon: Grid3X3, url: '/admin/room-grid', category: 'navigation' },
  { id: 'room-grid-lite', title: 'Шахматка Lite', icon: Zap, url: '/admin/room-grid-lite', category: 'navigation' },
  { id: 'bookings-list', title: 'Список броней', icon: CalendarRange, url: '/admin/bookings-list', category: 'navigation' },
  { id: 'rooms', title: 'Номера', icon: Building2, url: '/admin/rooms', category: 'navigation' },
  { id: 'offers', title: 'Предложения', icon: Percent, url: '/admin/offers', category: 'navigation' },
  { id: 'analytics', title: 'Аналитика', icon: BarChart3, url: '/admin/analytics', category: 'navigation' },
  { id: 'settings', title: 'Настройки', icon: Settings, url: '/admin/settings', category: 'navigation' },
  { id: 'users', title: 'Пользователи', icon: Users, url: '/admin/users', category: 'navigation' },
];

const getResultIcon = (type: string) => {
  switch (type) {
    case 'room': return Building2;
    case 'booking': return Calendar;
    case 'offer': return Percent;
    case 'guest': return Users;
    default: return Search;
  }
};

const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'confirmed': case 'active': return 'bg-green-500/10 text-green-700 dark:text-green-400';
    case 'pending': return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400';
    case 'cancelled': case 'inactive': return 'bg-red-500/10 text-red-700 dark:text-red-400';
    default: return 'bg-gray-500/10 text-gray-700 dark:text-gray-400';
  }
};

export const CommandPalette = () => {
  const navigate = useNavigate();
  const { 
    isCommandPaletteOpen, 
    setIsCommandPaletteOpen, 
    query, 
    setQuery, 
    results, 
    loading,
    recentSearches,
    addRecentSearch,
    clearRecentSearches,
    selectBookingFromSearch
  } = useSearchContext();

  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleSelect = (url: string, searchTerm?: string) => {
    if (searchTerm) {
      addRecentSearch(searchTerm);
    }
    setIsCommandPaletteOpen(false);
    setQuery('');
    navigate(url);
  };

  // Обработчик клика на бронирование — открывает модал вместо навигации
  const handleBookingSelect = (bookingId: string, bookingData: any, searchTerm?: string) => {
    if (searchTerm) {
      addRecentSearch(searchTerm);
    }
    // Переходим на страницу списка броней и открываем модал
    navigate('/admin/bookings-list');
    selectBookingFromSearch(bookingId, bookingData);
  };

  const filteredActions = query 
    ? quickActions.filter(action => 
        action.title.toLowerCase().includes(query.toLowerCase())
      )
    : quickActions;

  const filteredNavigation = query 
    ? navigationItems.filter(item => 
        item.title.toLowerCase().includes(query.toLowerCase())
      )
    : navigationItems;

  const showRecentSearches = !query && recentSearches.length > 0;
  const showQuickActions = filteredActions.length > 0;
  const showNavigation = filteredNavigation.length > 0;
  const showResults = results.length > 0;
  
  console.log('🔍 CommandPalette - результаты:', results.length, 'showResults:', showResults);

  return (
    <CommandDialog
      open={isCommandPaletteOpen}
      onOpenChange={setIsCommandPaletteOpen}
      contentClassName="max-h-[85vh] w-[calc(100vw-1rem)] sm:w-full sm:max-w-2xl pb-[env(safe-area-inset-bottom)] flex flex-col"
    >
      <div className="flex items-center border-b px-3">
        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
        <CommandInput
          placeholder="Поиск номеров, броней, предложений..."
          value={query}
          onValueChange={setQuery}
          className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
        />
        {query && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setQuery('')}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <CommandList className="max-h-[70vh] flex-1 overflow-y-auto">
        <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
          {loading ? 'Поиск...' : 'Ничего не найдено.'}
        </CommandEmpty>

        {/* Recent Searches */}
        {showRecentSearches && (
          <CommandGroup heading="Недавние поиски">
            <div className="flex items-center justify-between px-2 py-1">
              <span className="text-xs text-muted-foreground">Последние запросы</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearRecentSearches}
                className="h-auto p-1 text-xs text-muted-foreground hover:text-foreground"
              >
                Очистить
              </Button>
            </div>
            {recentSearches.map((search, index) => (
              <CommandItem
                key={`recent-${index}`}
                onSelect={() => setQuery(search)}
                className="flex items-center gap-2 px-2 py-1.5"
              >
                <Clock className="h-4 w-4 opacity-50" />
                <span className="truncate">{search}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Search Results */}
        {showResults && (
          <CommandGroup heading="Результаты поиска">
            {results.map((result) => {
              const Icon = getResultIcon(result.type);
              const data = result.data as any;
              
              // Для гостей: показываем количество бронирований вместо inactive
              // Статус определяем по типу результата
              let status: string;
              let statusLabel: string;
              
              if (result.type === 'guest') {
                // Извлекаем количество бронирований из description
                const bookingsMatch = result.description.match(/(\d+)\s*бронирован/);
                const hasBookings = bookingsMatch && parseInt(bookingsMatch[1]) > 0;
                const latestStatus = data.latest_booking_status;
                status = latestStatus || (hasBookings ? 'active' : 'pending');
                
                // Формируем метку: "1 брон." + статус последней брони
                const bookingsLabel = hasBookings ? `${bookingsMatch[1]} брон.` : 'Новый';
                const statusMap: Record<string, string> = {
                  confirmed: 'Подтверждено',
                  pending: 'Ожидание',
                  cancelled: 'Отменено',
                  checked_in: 'Заселён',
                  checked_out: 'Выселен',
                };
                const statusSuffix = latestStatus && statusMap[latestStatus] ? ` · ${statusMap[latestStatus]}` : '';
                statusLabel = bookingsLabel + statusSuffix;
              } else if (result.type === 'booking') {
                status = data.status || 'pending';
                const statusMap: Record<string, string> = {
                  confirmed: 'Подтверждено',
                  pending: 'Ожидание',
                  cancelled: 'Отменено',
                  checked_in: 'Заселён',
                  checked_out: 'Выселен',
                };
                statusLabel = statusMap[status] || status;
              } else {
                status = data.status || (data.is_available ? 'active' : 'inactive');
                statusLabel = status;
              }
              
              // Для бронирований и гостей — открываем модал, для остального — навигация
              const handleResultClick = async () => {
                if (result.type === 'booking') {
                  handleBookingSelect(result.id, result.data, query);
                } else if (result.type === 'guest') {
                  // Для гостя: ищем первую бронь и открываем модал
                  const guestId = data.guest_id || result.id;
                  
                  try {
                    const { data: firstBooking, error } = await supabase
                      .from('booking_core')
                      .select('*')
                      .eq('guest_id', guestId)
                      .is('deleted_at', null)
                      .order('check_in_date', { ascending: false })
                      .limit(1)
                      .maybeSingle();
                    
                    if (firstBooking && !error) {
                      // Используем существующий механизм открытия модала
                      handleBookingSelect(firstBooking.id, firstBooking, query);
                    } else {
                      // Нет бронирований — уведомляем пользователя
                      if (query) addRecentSearch(query);
                      setIsCommandPaletteOpen(false);
                      setQuery('');
                      navigate('/admin/bookings-list');
                      toast({
                        title: 'Бронирования не найдены',
                        description: `У гостя "${result.title}" нет активных бронирований`,
                      });
                    }
                  } catch (err) {
                    console.error('Ошибка поиска брони гостя:', err);
                    setIsCommandPaletteOpen(false);
                    navigate('/admin/bookings-list');
                  }
                } else {
                  handleSelect(result.url, query);
                }
              };

              return (
                <CommandItem
                  key={result.id}
                  onSelect={handleResultClick}
                  className="flex items-center gap-3 px-2 py-3"
                >
                  <Icon className="h-4 w-4 shrink-0 opacity-70" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{result.title}</span>
                      <Badge variant="secondary" className={getStatusColor(status)}>
                        {statusLabel}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {result.description}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {result.type === 'room' ? 'Номер' : 
                     result.type === 'booking' ? 'Бронь' : 
                     result.type === 'guest' ? 'Гость' : 'Предложение'}
                  </Badge>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {/* Quick Actions */}
        {showQuickActions && (
          <>
            {(showResults || showRecentSearches) && <CommandSeparator />}
            <CommandGroup heading="Быстрые действия">
              {filteredActions.map((action) => {
                const Icon = action.icon;
                return (
                  <CommandItem
                    key={action.id}
                    onSelect={() => handleSelect(action.url)}
                    className="flex items-center gap-3 px-2 py-2"
                  >
                    <Icon className="h-4 w-4 opacity-70" />
                    <span>{action.title}</span>
                    <Badge variant="outline" className="ml-auto text-xs">
                      Действие
                    </Badge>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}

        {/* Navigation */}
        {showNavigation && (
          <>
            {(showResults || showRecentSearches || showQuickActions) && <CommandSeparator />}
            <CommandGroup heading="Навигация">
              {filteredNavigation.map((item) => {
                const Icon = item.icon;
                return (
                  <CommandItem
                    key={item.id}
                    onSelect={() => handleSelect(item.url)}
                    className="flex items-center gap-3 px-2 py-2"
                  >
                    <Icon className="h-4 w-4 opacity-70" />
                    <span>{item.title}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}
      </CommandList>

      <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-4">
          <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium">
            <span className="text-xs">↵</span>Выбрать
          </kbd>
          <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium">
            <span className="text-xs">Esc</span>Закрыть
          </kbd>
        </div>
        <div className="text-right">
          <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium">
            ⌘K
          </kbd>
        </div>
      </div>
    </CommandDialog>
  );
};
